// front-end/src/components/block-system/block-manager/block-system.js

import {isAuthenticated} from '../../auth';
import {fetchBlocksByPage, fetchBlocksBySection} from './block-api';
import {createAndTrackBlock, initCarousel, loadContentForBlocks} from './block-renderer';
import {createSectionControls} from './block-controls';
import {isSectionLoaded, markSectionLoaded, setInitialized} from './block-state';
import {addNewBlock, deleteBlock} from './block-editor';
import {publishBlockChanges} from './block-publisher';
import {addDragAndDropStyles, initSortable, makeBlockDraggable} from './block-sortable';
import {getBaseType} from '../block-configs';
import {updateStepsNumbers} from '../../carousel';

function shouldProcessSection(section, sectionId) {
    const baseType = getBaseType(sectionId);

    console.log("Базовый тип секции ", baseType)

    // НЕ обрабатываем news-section на news-feed - там работает news-feed-loader
    if (baseType === 'news-section' && window.location.pathname.match(/news-feed(\.html)?$/)) {
        console.log(`[BlockSystem] Пропускаем секцию ${sectionId} - управляется news-feed-loader`);
        return false;
    }

    if (baseType === 'certificates-section') {
        console.log(`[BlockSystem] Пропускаем секцию ${sectionId} - управляется certificates-loader`);
        return false;
    }

    // НОВОЕ: НЕ обрабатываем courses-section - там работает courses-loader
    if (baseType === 'courses-section') {
        console.log(`[BlockSystem] Пропускаем секцию ${sectionId} - управляется courses-loader`);
        return false;
    }

    if (baseType === 'vacancies-section') {
        console.log(`[BlockSystem] Пропускаем секцию ${sectionId} - управляется vacancies-loader`);
        return false;
    }

    // vacancy-content is only relevant on /vacancy/{slug} pages
    if ((baseType === 'vacancy-content' || sectionId === 'vacancy-content') &&
        !window.location.pathname.match(/\/vacancy\//)) {
        return false;
    }

    if (baseType === 'cases-section') {
        console.log(`[BlockSystem] Пропускаем секцию ${sectionId} - управляется cases-loader`);
        return false;
    }

    // expertises-section завжди рендериться expertise-loader (слайдер), блок-система не втручається
    if (baseType === 'expertises-section') {
        console.log(`[BlockSystem] Пропускаем секцию ${sectionId} - управляется expertise-loader`);
        return false;
    }

    // НЕ обрабатываем секцию релевантных новостей на страницах новостей и кейсов
    if (window.location.pathname.match(/\/News(\.html)?$|\/news\/|\/case\//)) {
        const parentSection = section.closest('.news-relevant-feed-content-main');
        if (parentSection) {
            console.log(`[BlockSystem] Пропускаем секцию релевантных новостей - управляется news-article-loader`);
            return false;
        }
    }

    // НОВОЕ: НЕ обрабатываем accordion-cases на главной странице
    if (sectionId === 'accordion-cases-2' && document.body.getAttribute('data-page') === 'Home') {
        console.log(`[BlockSystem] Пропускаем секцию ${sectionId} - управляется home-cases-loader`);
        return false;
    }

    return true;
}

// Определяет, нужно ли загружать блоки по странице
function shouldLoadByPage(sectionId) {
    const baseType = getBaseType(sectionId);

    // Для news-content всегда загружаем по странице
    if (baseType === 'news-content' || sectionId === 'news-content') {
        return true;
    }

    // НОВОЕ: Для course-modules-section тоже загружаем по странице
    if (baseType === 'course-modules-section' || sectionId === 'course-modules-section') {
        return true;
    }

    // Для diagnostic-content завжди завантажуємо по сторінці
    if (baseType === 'diagnostic-content' || sectionId === 'diagnostic-content') {
        return true;
    }

    if (baseType === 'vacancy-content' || sectionId === 'vacancy-content') {
        return true;
    }

    return false;
}

// Определяет название страницы для загрузки блоков
function getPageTitleForSection(sectionId) {
    const path = window.location.pathname;

    // Clean URL: /news/{slug}
    const newsPathMatch = path.match(/^\/news\/([^/]+)/);
    if (newsPathMatch) {
        return `News - ${newsPathMatch[1]}`;
    }

    // Clean URL: /case/{slug}
    const casePathMatch = path.match(/^\/case\/([^/]+)/);
    if (casePathMatch) {
        return `Case - ${casePathMatch[1]}`;
    }

    // Clean URL: /course/{slug}
    const coursePathMatch = path.match(/^\/course\/([^/]+)/);
    if (coursePathMatch) {
        return `Course - ${coursePathMatch[1]}`;
    }

    // Clean URL: /diagnostic/{slug}
    const diagPathMatch = path.match(/^\/diagnostic\/([^/]+)/);
    if (diagPathMatch) {
        return `Diagnostic - ${diagPathMatch[1]}`;
    }

    // Clean URL: /vacancy/{slug}
    const vacancyPathMatch = path.match(/^\/vacancy\/([^/]+)/);
    if (vacancyPathMatch) {
        return `Vacancy - ${vacancyPathMatch[1]}`;
    }

    // Legacy URL: /News.html?slug=X or /News?slug=X
    if (path.match(/\/News(\.html)?$/)) {
        const newsSlug = new URLSearchParams(window.location.search).get('slug');
        if (newsSlug) {
            return newsSlug.startsWith('case-') ? `Case - ${newsSlug}` : `News - ${newsSlug}`;
        }
    }

    // Legacy URL: /course-detail.html?slug=X
    if (path.match(/course-detail(\.html)?$/)) {
        const courseSlug = new URLSearchParams(window.location.search).get('slug');
        if (courseSlug) return `Course - ${courseSlug}`;
    }

    // Legacy URL: /diagnostic-detail.html?slug=X
    if (path.match(/diagnostic-detail(\.html)?$/)) {
        const diagnosticSlug = new URLSearchParams(window.location.search).get('slug');
        if (diagnosticSlug) return `Diagnostic - ${diagnosticSlug}`;
    }

    const bodyPage = document.body.getAttribute('data-page');
    return bodyPage || 'Home';
}

// Проверяем, есть ли уже контролы у блока
function hasBlockControls(blockElement) {
    return blockElement.querySelector('.block-delete-button') !== null;
}

// Добавляем контролы к блоку (с проверкой на дубликаты)
function addBlockControlsSafe(blockElement) {
    if (!blockElement || !isAuthenticated()) return;

    // ВАЖНО: Проверяем, что это действительно блок, а не элемент контента
    if (!blockElement.hasAttribute('data-block-id')) {
        console.log('[BlockSystem] Элемент не является блоком, пропускаем добавление контролов');
        return;
    }

    const blockId = blockElement.getAttribute('data-block-id');
    if (!blockId) return;

    // Проверяем, есть ли уже кнопка удаления
    if (!hasBlockControls(blockElement)) {
        const {addDeleteButton} = require('./block-editor');
        addDeleteButton(blockElement, blockId);
    }

    // Делаем блок перетаскиваемым, если еще не сделали
    if (!blockElement.hasAttribute('draggable')) {
        blockElement.setAttribute('draggable', 'true');
        makeBlockDraggable(blockElement);
    }
}

export async function initBlockSystem() {
    const sections = document.querySelectorAll('[data-section]');
    if (!sections.length) {
        return;
    }

    try {
        for (const section of sections) {
            const sectionId = section.id || section.getAttribute('class');
            console.log(`[BlockSystem] Проверяем секцию: ${sectionId}`);

            // Проверяем, нужно ли обрабатывать эту секцию
            if (!shouldProcessSection(section, sectionId)) {
                if (isAuthenticated()) {
                    const baseType = getBaseType(sectionId);
                    const skipControls =
                        (sectionId === 'accordion-cases-2' && document.body.getAttribute('data-page') === 'Home') ||
                        baseType === 'expertises-section';
                    if (!skipControls) {
                        createSectionControls(section);
                    }
                }
                continue;
            }

            console.log(`[BlockSystem] Обрабатываем секцию: ${sectionId}`);

            if (section.dataset.ssr === 'true') {
                console.log(`[BlockSystem] SSR контент для ${sectionId}, пропускаємо завантаження`);
                markSectionLoaded(sectionId);
                loadContentForBlocks(section);
                if (isAuthenticated()) {
                    createSectionControls(section);
                    initSortable(section);
                }
                continue;
            }

            if (!isSectionLoaded(sectionId)) {
                let blocks = [];

                if (shouldLoadByPage(sectionId)) {
                    const pageTitle = getPageTitleForSection(sectionId);
                    console.log(`[BlockSystem] Загружаем блоки по странице для ${sectionId}, страница: ${pageTitle}`);
                    blocks = await fetchBlocksByPage(pageTitle);
                } else {
                    console.log(`[BlockSystem] Загружаем блоки по секции для ${sectionId}`);
                    blocks = await fetchBlocksBySection(sectionId);
                }

                if (blocks && blocks.length) {
                    const sortedBlocks = blocks.sort((a, b) => {
                        const orderA = a.order !== undefined ? parseInt(a.order, 10) : parseInt(a.id, 10);
                        const orderB = b.order !== undefined ? parseInt(b.order, 10) : parseInt(b.id, 10);
                        return orderA - orderB;
                    });

                    console.log('[BlockSystem] Отсортированные блоки:', sortedBlocks);

                    const nonBlockElements = Array.from(section.children).filter(
                        child => !child.hasAttribute('data-block-name')
                    );

                    section.innerHTML = '';
                    nonBlockElements.forEach(element => section.appendChild(element));

                    const createdBlocks = [];
                    sortedBlocks.forEach(block => {
                        const createdBlock = createAndTrackBlock(block, section);
                        if (createdBlock) {
                            createdBlocks.push(createdBlock);
                        }
                    });

                    // Добавляем контролы к созданным блокам
                    if (isAuthenticated()) {
                        console.log(`[BlockSystem] Добавляем контролы к ${createdBlocks.length} блокам в секции ${sectionId}`);
                        createdBlocks.forEach(blockElement => {
                            addBlockControlsSafe(blockElement);
                        });
                    }
                }

                markSectionLoaded(sectionId);
            }

            loadContentForBlocks(section);

            // Показываем контролы секции при аутентификации
            if (isAuthenticated()) {
                createSectionControls(section);
                initSortable(section);
            }

            if (section.classList.contains('diagnostic-alghorithm')) {
                updateStepsNumbers(section);
            }
        }

        setInitialized(true);

        if (isAuthenticated()) {
            addDragAndDropStyles();

            // Финальная проверка контролов (с защитой от дубликатов)
            setTimeout(() => {
                console.log('[BlockSystem] Финальная проверка контролов блоков...');
                const allBlocks = document.querySelectorAll('[data-block-id]');
                allBlocks.forEach(blockElement => {
                    // НОВАЯ ПРОВЕРКА: Пропускаем элементы контента
                    if (blockElement.hasAttribute('data-content')) {
                        console.log('[BlockSystem] Пропускаем элемент контента:', blockElement.getAttribute('data-content'));
                        return;
                    }

                    // Пропускаем блоки в news-section на news-feed
                    const section = blockElement.closest('[data-section]');
                    if (section) {
                        const sectionId = section.id || section.getAttribute('class');
                        const baseType = getBaseType(sectionId);
                        if (baseType === 'news-section' && window.location.pathname.match(/news-feed(\.html)?$/)) {
                            console.log(`[BlockSystem] Пропускаем блок в news-section на news-feed`);
                            return;
                        }
                        // Пропускаем блоки в accordion на главной
                        if (sectionId === 'accordion-cases-2' && document.body.getAttribute('data-page') === 'Home') {
                            console.log(`[BlockSystem] Пропускаем блок в accordion-cases на главной`);
                            return;
                        }
                    }

                    addBlockControlsSafe(blockElement);
                });
            }, 500);
        }

        setTimeout(initCarousel, 0);
    } catch (error) {
        console.error('[BlockSystem] Ошибка инициализации:', error);
    }
}

export function processNewBlock(blockElement) {
    if (!blockElement) return;

    loadContentForBlocks(blockElement);

    if (isAuthenticated()) {
        addBlockControlsSafe(blockElement);
    }

    const section = blockElement.closest('[data-section="carousel"].diagnostic-alghorithm');
    if (section) {
        updateStepsNumbers(section);
    }
}

export const blockSystemAPI = {
    addBlock: addNewBlock,
    publishChanges: publishBlockChanges,
    deleteBlock: deleteBlock,
    initBlockSystem,
    reinitCarousel: initCarousel,
    processNewBlock: processNewBlock,
    makeDraggable: makeBlockDraggable
};